import { useEffect } from 'react';
import { TASK_LIVE_FORBIDDEN, type TaskLiveMessage } from '@shared/types/tasks';
import { useAuthStore } from '@/stores/authStore';
import { useTaskStore } from '@/stores/taskStore';
import { useProductContentStore } from '@/stores/productContentStore';
import { usePermission } from '@/hooks/usePermission';
import { getTaskNotifications } from '@/lib/tasks-api';
import { receiveLiveChange, resyncLiveChanges } from '@/lib/live-changes';

const PING_MS = 30_000;

function refreshContentQueue(): void {
  void useProductContentStore.getState().fetchSummary({ force: true });
}
const MAX_BACKOFF_MS = 30_000;

function liveUrl(token: string): string {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}/api/tasks/live?token=${encodeURIComponent(token)}`;
}

/**
 * One websocket per tab to the team's live hub. Mounted once in the team layout; it bumps ticks
 * in the task store (the hooks that care refetch), refreshes the content-queue count, and hands
 * `data` and `record` messages to `lib/live-changes`. While it's down, `LiveUpdatesIndicator` says so.
 * A hidden tab doesn't reconnect: each try reads the database, so a tab left open overnight would keep
 * it awake (T-339). It reconnects as soon as the tab is shown. A refused socket (TASK_LIVE_FORBIDDEN)
 * isn't retried at all.
 */
export function TaskLiveConnector() {
  const canView = usePermission('TASKS');
  const teamId = useAuthStore((s) => s.user?.active_team?.id);
  const teamActive = useAuthStore((s) => s.user?.active_team?.status === 'active');

  useEffect(() => {
    if (!canView || !teamId || !teamActive) return undefined;
    const store = useTaskStore.getState();
    let socket: WebSocket | null = null;
    let ping: ReturnType<typeof setInterval> | undefined;
    let retry: ReturnType<typeof setTimeout> | undefined;
    let attempts = 0;
    let stopped = false;
    let hasConnected = false;
    /** The socket dropped while the tab was hidden: reconnect when it's shown. */
    let waitingForVisible = false;

    const connect = () => {
      const token = useAuthStore.getState().user?.token;
      if (!token || stopped) return;
      socket = new WebSocket(liveUrl(token));
      socket.onopen = () => {
        // A reconnect may have missed changes, and so may a first connect after failed tries (the
        // page loaded over HTTP meanwhile); a first connect at once loads fresh anyway.
        const mayHaveMissed = hasConnected || attempts > 0;
        attempts = 0;
        store.setConnected(true);
        if (mayHaveMissed) {
          store.resync();
          refreshContentQueue();
          resyncLiveChanges(teamId);
        }
        hasConnected = true;
        ping = setInterval(() => socket?.readyState === WebSocket.OPEN && socket.send('ping'), PING_MS);
      };
      socket.onmessage = (event) => {
        if (event.data === 'pong') return;
        let message: TaskLiveMessage;
        try {
          message = JSON.parse(String(event.data)) as TaskLiveMessage;
        } catch {
          return; // Not one of ours.
        }
        // The queued Shopify content changed (someone staged, discarded or sent it).
        if (message.type === 'product_content') refreshContentQueue();
        // Synced data or a record changed (data-freshness.mdc).
        else if (message.type === 'data' || message.type === 'record') receiveLiveChange(teamId, message);
        else store.receive(message);
      };
      socket.onclose = (event) => {
        clearInterval(ping);
        // A socket closed on purpose (team switch, sign-out) can close after the next one opened:
        // it mustn't mark that one down.
        if (stopped) return;
        if (event.code === TASK_LIVE_FORBIDDEN) {
          // No task access any more (the next token renewal hides tasks too): not an outage.
          stopped = true;
          store.setConnected(false, { idle: true });
          return;
        }
        store.setConnected(false);
        attempts += 1;
        const delay = Math.min(MAX_BACKOFF_MS, 1000 * 2 ** Math.min(attempts, 5));
        retry = setTimeout(reconnect, delay);
      };
    };

    // An expired token closes the socket; any API call refreshes it before we retry.
    const reconnect = () => {
      if (stopped) return;
      if (document.hidden) {
        waitingForVisible = true;
        return;
      }
      void getTaskNotifications().finally(connect);
    };
    const onVisibility = () => {
      if (document.hidden || !waitingForVisible) return;
      waitingForVisible = false;
      clearTimeout(retry);
      reconnect();
    };

    document.addEventListener('visibilitychange', onVisibility);
    connect();
    return () => {
      stopped = true;
      document.removeEventListener('visibilitychange', onVisibility);
      clearInterval(ping);
      clearTimeout(retry);
      socket?.close();
      // Closing on purpose (sign-out, team switch): not an outage to report.
      store.setConnected(false, { idle: true });
    };
  }, [canView, teamId, teamActive]);

  return null;
}
